import { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Underline } from '@tiptap/extension-underline';
import { Link } from '@tiptap/extension-link';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Toggle } from '@/components/ui/toggle';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Table as TableIcon,
  Undo,
  Redo,
  Heading2,
  Heading3,
  RemoveFormatting,
  Link2,
  Unlink,
  Plus,
  Minus,
  Trash2,
  ImageIcon,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { ImageUploadButton } from '@low6-pickem/ui';
import { ImageGalleryPickerButton } from '@/components/styling/ImageGalleryPicker';
import { uploadImage } from '@/api/upload/uploadImage';
import { cn } from '@/lib/utils';

interface RichTextEditorImageInsertConfig {
  tenantCode: string;
  uploadPath?: string;
}

interface RichTextEditorProps {
  content: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  placeholder?: string;
  imageInsert?: RichTextEditorImageInsertConfig;
}

export function RichTextEditor({
  content,
  onChange,
  disabled = false,
  placeholder,
  imageInsert,
}: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: 'https',
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          target: '_blank',
          class: 'text-primary underline underline-offset-2',
        },
      }),
      Table.configure({ resizable: false }),
      TableRow,
      TableCell,
      TableHeader,
    ],
    content,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-invert prose-sm max-w-none min-h-[200px] p-3 focus:outline-none',
      },
    },
  });

  // Sync external content changes into the editor (e.g. switching versions, data refetch)
  const skipNextSync = useRef(false);
  useEffect(() => {
    if (!editor) return;
    if (skipNextSync.current) {
      skipNextSync.current = false;
      return;
    }
    if (content !== editor.getHTML()) {
      editor.commands.setContent(content, { emitUpdate: false });
    }
  }, [content, editor]);

  // Mark internal updates so we don't fight the user's typing
  useEffect(() => {
    if (!editor) return;
    const handler = () => {
      skipNextSync.current = true;
    };
    editor.on('update', handler);
    return () => {
      editor.off('update', handler);
    };
  }, [editor]);

  if (!editor) return null;

  const insertImage = (url: string) => {
    editor.chain().focus().insertContent(`<img src="${url}" alt="" />`).run();
  };

  const imageUploadPath =
    imageInsert?.uploadPath ?? `marketing-consent/${imageInsert?.tenantCode ?? 'shared'}/body`;

  return (
    <div className="border rounded-md overflow-hidden bg-background">
      <TooltipProvider delayDuration={300}>
        <div className="flex flex-wrap gap-0.5 border-b p-1 bg-muted/50">
          <ToolbarButton
            tooltip="Bold"
            pressed={editor.isActive('bold')}
            onPressedChange={() => editor.chain().focus().toggleBold().run()}
            disabled={disabled}
          >
            <Bold className="h-4 w-4" />
          </ToolbarButton>

          <ToolbarButton
            tooltip="Italic"
            pressed={editor.isActive('italic')}
            onPressedChange={() => editor.chain().focus().toggleItalic().run()}
            disabled={disabled}
          >
            <Italic className="h-4 w-4" />
          </ToolbarButton>

          <ToolbarButton
            tooltip="Underline"
            pressed={editor.isActive('underline')}
            onPressedChange={() => editor.chain().focus().toggleUnderline().run()}
            disabled={disabled}
          >
            <UnderlineIcon className="h-4 w-4" />
          </ToolbarButton>

          <div className="w-px bg-border mx-1" />

          <ToolbarButton
            tooltip="Heading 2"
            pressed={editor.isActive('heading', { level: 2 })}
            onPressedChange={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            disabled={disabled}
          >
            <Heading2 className="h-4 w-4" />
          </ToolbarButton>

          <ToolbarButton
            tooltip="Heading 3"
            pressed={editor.isActive('heading', { level: 3 })}
            onPressedChange={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            disabled={disabled}
          >
            <Heading3 className="h-4 w-4" />
          </ToolbarButton>

          <div className="w-px bg-border mx-1" />

          <ToolbarButton
            tooltip="Bullet List"
            pressed={editor.isActive('bulletList')}
            onPressedChange={() => editor.chain().focus().toggleBulletList().run()}
            disabled={disabled}
          >
            <List className="h-4 w-4" />
          </ToolbarButton>

          <ToolbarButton
            tooltip="Ordered List"
            pressed={editor.isActive('orderedList')}
            onPressedChange={() => editor.chain().focus().toggleOrderedList().run()}
            disabled={disabled}
          >
            <ListOrdered className="h-4 w-4" />
          </ToolbarButton>

          <div className="w-px bg-border mx-1" />

          <ToolbarActionButton
            tooltip="Add or edit hyperlink. Leave the URL empty and confirm to remove the link from the selection."
            onClick={() => {
              const previousUrl = (editor.getAttributes('link').href as string | undefined) ?? '';
              const url = window.prompt('Link URL (leave empty to remove)', previousUrl);
              if (url === null) return;
              const trimmed = url.trim();
              if (trimmed === '') {
                editor.chain().focus().extendMarkRange('link').unsetLink().run();
                return;
              }
              editor.chain().focus().extendMarkRange('link').setLink({ href: trimmed }).run();
            }}
            disabled={disabled}
            active={editor.isActive('link')}
          >
            <Link2 className="h-4 w-4" />
          </ToolbarActionButton>

          <ToolbarActionButton
            tooltip="Remove hyperlink from the selection"
            onClick={() => editor.chain().focus().extendMarkRange('link').unsetLink().run()}
            disabled={disabled || !editor.isActive('link')}
            active={false}
          >
            <Unlink className="h-4 w-4" />
          </ToolbarActionButton>

          <div className="w-px bg-border mx-1" />

          {imageInsert ? (
            <>
              <ImageInsertButton
                disabled={disabled}
                onInsert={insertImage}
                tenantCode={imageInsert.tenantCode}
                uploadPath={imageUploadPath}
              />
              <div className="w-px bg-border mx-1" />
            </>
          ) : null}

          <TableInsertButton editor={editor} disabled={disabled} />

          {editor.isActive('table') && (
            <>
              <ToolbarActionButton
                tooltip="Add row below"
                onClick={() => editor.chain().focus().addRowAfter().run()}
                disabled={disabled}
              >
                <div className="flex items-center gap-px">
                  <Plus className="h-3 w-3" />
                  <span className="text-[10px] font-medium">R</span>
                </div>
              </ToolbarActionButton>

              <ToolbarActionButton
                tooltip="Delete row"
                onClick={() => editor.chain().focus().deleteRow().run()}
                disabled={disabled}
              >
                <div className="flex items-center gap-px">
                  <Minus className="h-3 w-3" />
                  <span className="text-[10px] font-medium">R</span>
                </div>
              </ToolbarActionButton>

              <ToolbarActionButton
                tooltip="Add column after"
                onClick={() => editor.chain().focus().addColumnAfter().run()}
                disabled={disabled}
              >
                <div className="flex items-center gap-px">
                  <Plus className="h-3 w-3" />
                  <span className="text-[10px] font-medium">C</span>
                </div>
              </ToolbarActionButton>

              <ToolbarActionButton
                tooltip="Delete column"
                onClick={() => editor.chain().focus().deleteColumn().run()}
                disabled={disabled}
              >
                <div className="flex items-center gap-px">
                  <Minus className="h-3 w-3" />
                  <span className="text-[10px] font-medium">C</span>
                </div>
              </ToolbarActionButton>

              <ToolbarActionButton
                tooltip="Delete table"
                onClick={() => editor.chain().focus().deleteTable().run()}
                disabled={disabled}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </ToolbarActionButton>
            </>
          )}

          <ToolbarButton
            tooltip="Clear Formatting"
            pressed={false}
            onPressedChange={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
            disabled={disabled}
          >
            <RemoveFormatting className="h-4 w-4" />
          </ToolbarButton>

          <div className="w-px bg-border mx-1" />

          <ToolbarButton
            tooltip="Undo"
            pressed={false}
            onPressedChange={() => editor.chain().focus().undo().run()}
            disabled={disabled || !editor.can().undo()}
          >
            <Undo className="h-4 w-4" />
          </ToolbarButton>

          <ToolbarButton
            tooltip="Redo"
            pressed={false}
            onPressedChange={() => editor.chain().focus().redo().run()}
            disabled={disabled || !editor.can().redo()}
          >
            <Redo className="h-4 w-4" />
          </ToolbarButton>
        </div>
      </TooltipProvider>

      <EditorContent editor={editor} />

      {!content && placeholder && (
        <style>{`
          .tiptap p.is-editor-empty:first-child::before {
            content: '${placeholder}';
            float: left;
            color: hsl(var(--muted-foreground));
            pointer-events: none;
            height: 0;
          }
        `}</style>
      )}

      <style>{`
        .tiptap { min-height: 200px; }
        .tiptap:focus { outline: none; }
        .tiptap h2 { font-size: 1.25rem; font-weight: 700; margin: 0.75rem 0 0.5rem; }
        .tiptap h3 { font-size: 1.1rem; font-weight: 600; margin: 0.5rem 0 0.25rem; }
        .tiptap p { margin: 0.25rem 0; }
        .tiptap ul, .tiptap ol { padding-left: 1.5rem; margin: 0.25rem 0; }
        .tiptap ul { list-style-type: disc; }
        .tiptap ol { list-style-type: decimal; }
        .tiptap li { margin: 0.125rem 0; }
        .tiptap table { border-collapse: collapse; width: 100%; margin: 0.5rem 0; }
        .tiptap th, .tiptap td { border: 1px solid hsl(var(--border)); padding: 0.375rem 0.75rem; text-align: left; }
        .tiptap th { font-weight: 600; background: hsl(var(--muted)); }
        .tiptap a { cursor: pointer; }
        .tiptap img { max-width: 100%; height: auto; border-radius: 0.375rem; margin: 0.5rem 0; }
      `}</style>
    </div>
  );
}

function ToolbarButton({
  children,
  tooltip,
  pressed,
  onPressedChange,
  disabled,
}: {
  children: React.ReactNode;
  tooltip: string;
  pressed: boolean;
  onPressedChange: () => void;
  disabled?: boolean;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Toggle
          size="sm"
          pressed={pressed}
          onPressedChange={onPressedChange}
          disabled={disabled}
          className="h-8 w-8 p-0"
        >
          {children}
        </Toggle>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="text-xs">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}

function ToolbarActionButton({
  children,
  tooltip,
  onClick,
  disabled,
  active,
}: {
  children: React.ReactNode;
  tooltip: string;
  onClick: () => void;
  disabled?: boolean;
  active?: boolean;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          className={cn(
            'inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors h-8 w-8 p-0',
            'hover:bg-muted hover:text-muted-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
            'disabled:pointer-events-none disabled:opacity-50',
            active && 'bg-accent text-accent-foreground'
          )}
        >
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="text-xs max-w-xs">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}

const MAX_ROWS = 10;
const MAX_COLS = 5;

function ImageInsertButton({
  disabled,
  onInsert,
  tenantCode,
  uploadPath,
}: {
  disabled?: boolean;
  onInsert: (url: string) => void;
  tenantCode: string;
  uploadPath: string;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              className={cn(
                'inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors h-8 w-8 p-0',
                'hover:bg-muted hover:text-muted-foreground',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                'disabled:pointer-events-none disabled:opacity-50'
              )}
            >
              <ImageIcon className="h-4 w-4" />
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs">
          Insert Image
        </TooltipContent>
      </Tooltip>
      <PopoverContent className="w-auto p-3" side="bottom" align="start">
        <p className="mb-2 text-xs text-muted-foreground">Insert image into content</p>
        <div className="flex items-center gap-2">
          <ImageUploadButton
            onUpload={url => {
              onInsert(url);
              setOpen(false);
            }}
            path={uploadPath}
            filename="inline-image"
            disabled={disabled}
            uploadFn={async data => {
              const result = await uploadImage(data);
              return { imageUrl: result.imageUrl };
            }}
            title="Upload image"
          />
          <ImageGalleryPickerButton
            tenantCode={tenantCode}
            disabled={disabled}
            onSelect={url => {
              onInsert(url);
              setOpen(false);
            }}
          />
          <Button type="button" variant="outline" size="sm" onClick={() => setOpen(false)}>
            Cancel
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function TableInsertButton({
  editor,
  disabled,
}: {
  editor: ReturnType<typeof useEditor>;
  disabled?: boolean;
}) {
  const [hoverRow, setHoverRow] = useState(0);
  const [hoverCol, setHoverCol] = useState(0);
  const [open, setOpen] = useState(false);

  if (!editor) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              className={cn(
                'inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors h-8 w-8 p-0',
                'hover:bg-muted hover:text-muted-foreground',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                'disabled:pointer-events-none disabled:opacity-50'
              )}
            >
              <TableIcon className="h-4 w-4" />
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs">
          Insert Table
        </TooltipContent>
      </Tooltip>
      <PopoverContent className="w-auto p-2" side="bottom" align="start">
        <p className="text-xs text-muted-foreground mb-1.5 text-center">
          {hoverRow > 0 ? `${hoverRow} × ${hoverCol}` : 'Select size'}
        </p>
        <div
          className="grid gap-0.5"
          style={{ gridTemplateColumns: `repeat(${MAX_COLS}, 1fr)` }}
          onMouseLeave={() => {
            setHoverRow(0);
            setHoverCol(0);
          }}
        >
          {Array.from({ length: MAX_ROWS * MAX_COLS }, (_, i) => {
            const row = Math.floor(i / MAX_COLS) + 1;
            const col = (i % MAX_COLS) + 1;
            const active = row <= hoverRow && col <= hoverCol;
            return (
              <button
                key={i}
                type="button"
                className={cn(
                  'w-5 h-5 rounded-sm border transition-colors',
                  active
                    ? 'bg-primary border-primary'
                    : 'bg-muted/50 border-border hover:border-muted-foreground'
                )}
                onMouseEnter={() => {
                  setHoverRow(row);
                  setHoverCol(col);
                }}
                onClick={() => {
                  editor
                    .chain()
                    .focus()
                    .insertTable({ rows: row, cols: col, withHeaderRow: true })
                    .run();
                  setOpen(false);
                  setHoverRow(0);
                  setHoverCol(0);
                }}
              />
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
